import { Deferred, Head, router } from "@inertiajs/react";
import { ArrowRight, LoaderIcon, RefreshCw, TriangleAlert } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { OrderChangeOwnerDialog } from "@/components/admin-portal/order-ownership/change-owner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	Pagination,
	PaginationContent,
	PaginationItem,
	PaginationNext,
	PaginationPrevious,
} from "@/components/ui/pagination";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { getCsrfToken } from "@/lib/csrf";
import type {
	MislinkedOrder,
	MislinkedOrdersProp,
	RepairResult,
} from "@/types/admin-portal/order-ownership";

interface PageProps {
	mislinkedOrders?: MislinkedOrdersProp;
}

export default function Index({ mislinkedOrders }: PageProps) {
	const { t } = useTranslation("order-ownership");

	const [selected, setSelected] = useState<string[]>([]);
	const [preview, setPreview] = useState<RepairResult | null>(null);
	const [previewing, setPreviewing] = useState(false);
	const [repairing, setRepairing] = useState(false);
	const [changeOwnerTarget, setChangeOwnerTarget] =
		useState<MislinkedOrder | null>(null);

	// Guards the stale-preview race: pick A, click Preview, switch to B while A is in
	// flight, and A's late response would otherwise arm the destructive button with the
	// wrong count. The preview is bound to the selection that requested it.
	const previewTokenRef = useRef("");
	const tokenOf = (ids: string[]) => [...ids].sort().join(",");

	const rows = mislinkedOrders?.data ?? [];
	const loadError = mislinkedOrders?.error ?? null;
	const metadata = mislinkedOrders?.metadata;
	const currentPage = metadata?.page ?? 1;
	const totalPages = metadata?.totalPages ?? 1;

	function toggle(orderId: string) {
		setSelected((prev) => {
			const next = prev.includes(orderId)
				? prev.filter((id) => id !== orderId)
				: [...prev, orderId];
			// Any change to the selection invalidates the preview it was computed for.
			setPreview(null);
			previewTokenRef.current = "";
			return next;
		});
	}

	function toggleAll() {
		setSelected((prev) => {
			const next =
				prev.length === rows.length ? [] : rows.map((r) => r.orderId);
			setPreview(null);
			previewTokenRef.current = "";
			return next;
		});
	}

	async function onPreview() {
		if (!selected.length) return;
		const token = tokenOf(selected);
		previewTokenRef.current = token;
		setPreviewing(true);
		try {
			const res = await fetch("/admin-portal/order-ownership/repair_preview", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
					"X-CSRF-Token": getCsrfToken(),
				},
				body: JSON.stringify({ order_ids: selected }),
			});
			const data = await res.json();
			// The selection moved on while this was in flight — drop the answer.
			if (previewTokenRef.current !== token) return;
			if (res.ok && data.success) {
				setPreview(data.data as RepairResult);
			} else {
				toast.error(data.error || t("messages.error"));
			}
		} catch {
			// The selection moved on while this was in flight — don't toast for a
			// request the user has already abandoned.
			if (previewTokenRef.current === token) {
				toast.error(t("messages.error"));
			}
		} finally {
			setPreviewing(false);
		}
	}

	async function onRepair() {
		if (!selected.length || !preview) return;
		setRepairing(true);
		try {
			const res = await fetch("/admin-portal/order-ownership/repair", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
					"X-CSRF-Token": getCsrfToken(),
				},
				body: JSON.stringify({ order_ids: selected }),
			});
			const data = await res.json();
			if (res.ok && data.success) {
				const result = data.data as RepairResult;
				const updated = result.updated ?? 0;
				// Go re-applies the mislinked predicate inside the UPDATE, so an order fixed by
				// someone else between preview and apply is skipped. Report that honestly rather
				// than implying every selected row moved.
				toast.success(
					t("result.summary", {
						count: selected.length,
						selected: selected.length,
						updated,
						skipped: selected.length - updated,
					}),
				);
				setSelected([]);
				setPreview(null);
				previewTokenRef.current = "";
				router.reload();
			} else {
				toast.error(data.error || t("messages.error"));
			}
		} catch {
			toast.error(t("messages.error"));
		} finally {
			setRepairing(false);
		}
	}

	function goToPage(page: number) {
		// Page 2's rows are different orders; a carried-over selection or preview
		// would target rows no longer visible on the new page.
		setSelected([]);
		setPreview(null);
		previewTokenRef.current = "";
		router.get(
			window.location.pathname,
			{ page },
			{ only: ["mislinkedOrders"], preserveScroll: true, preserveState: true },
		);
	}

	return (
		<>
			<Head title={t("title")} />

			<div className="flex flex-col gap-4 p-4">
				<div>
					<h1 className="text-2xl font-bold tracking-tight">{t("title")}</h1>
					<p className="text-sm text-muted-foreground">{t("description")}</p>
				</div>

				<Alert>
					<TriangleAlert className="size-4" />
					<AlertTitle>{t("explainer.title")}</AlertTitle>
					<AlertDescription>{t("explainer.body")}</AlertDescription>
				</Alert>

				<Deferred
					data="mislinkedOrders"
					fallback={
						<div className="flex items-center gap-2 p-8 text-sm text-muted-foreground">
							<LoaderIcon className="size-4 animate-spin" />
						</div>
					}
				>
					{loadError ? (
						// A failed proxy must never look like an empty list: "nothing is broken" and
						// "we cannot reach the backend" are opposite conclusions.
						<Alert variant="destructive">
							<TriangleAlert className="size-4" />
							<AlertTitle>{t("failed.title")}</AlertTitle>
							<AlertDescription className="flex flex-col items-start gap-3">
								<span>{t("failed.body")}</span>
								<Button
									variant="outline"
									size="sm"
									onClick={() => router.reload()}
								>
									<RefreshCw className="size-4" />
									{t("failed.retry")}
								</Button>
							</AlertDescription>
						</Alert>
					) : rows.length === 0 ? (
						<div className="rounded-lg border border-dashed p-10 text-center">
							<p className="font-medium">{t("empty.title")}</p>
							<p className="text-sm text-muted-foreground">{t("empty.body")}</p>
						</div>
					) : (
						<>
							{selected.length > 0 && (
								<div className="flex items-center justify-between rounded-lg border bg-muted/40 p-3">
									<span className="text-sm font-medium">
										{t("selection", { count: selected.length })}
									</span>
									<Button onClick={onPreview} disabled={previewing}>
										{previewing && (
											<LoaderIcon className="size-4 animate-spin" />
										)}
										{t("actions.preview")}
									</Button>
								</div>
							)}

							<div className="rounded-lg border">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead className="w-10">
												<Checkbox
													checked={
														rows.length > 0 && selected.length === rows.length
													}
													onCheckedChange={toggleAll}
													aria-label={t("actions.select_all")}
												/>
											</TableHead>
											<TableHead>{t("table.registration_number")}</TableHead>
											<TableHead>{t("table.patient")}</TableHead>
											<TableHead>{t("table.current_owner")}</TableHead>
											<TableHead className="w-8" />
											<TableHead>{t("table.suggested_owner")}</TableHead>
											<TableHead className="w-10" />
										</TableRow>
									</TableHeader>
									<TableBody>
										{rows.map((row) => (
											<TableRow key={row.orderId}>
												<TableCell>
													<Checkbox
														checked={selected.includes(row.orderId)}
														onCheckedChange={() => toggle(row.orderId)}
														aria-label={t("actions.select_row", {
															reg: row.registrationNumber,
														})}
													/>
												</TableCell>
												<TableCell className="font-medium">
													{row.registrationNumber}
												</TableCell>
												<TableCell>{row.patientName}</TableCell>
												<TableCell>
													<div className="flex items-center gap-2">
														<span className="text-sm">
															{row.currentOwnerEmail || t("table.no_email")}
														</span>
														<Badge variant="secondary">
															{t("table.admin_badge")}
														</Badge>
													</div>
												</TableCell>
												<TableCell>
													<ArrowRight className="size-4 text-muted-foreground" />
												</TableCell>
												<TableCell className="text-sm">
													{row.suggestedEmail || t("table.no_email")}
												</TableCell>
												<TableCell>
													<Button
														variant="ghost"
														size="sm"
														onClick={() => setChangeOwnerTarget(row)}
													>
														{t("actions.change_owner")}
													</Button>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>

							{totalPages > 1 && (
								<Pagination className="justify-end">
									<PaginationContent>
										<PaginationItem>
											<PaginationPrevious
												className="cursor-pointer aria-disabled:pointer-events-none aria-disabled:opacity-50"
												aria-disabled={currentPage <= 1}
												onClick={() => {
													if (currentPage > 1) goToPage(currentPage - 1);
												}}
											/>
										</PaginationItem>
										<PaginationItem>
											<span className="px-2 text-sm text-muted-foreground">
												{t("pagination.info", {
													page: currentPage,
													total: totalPages,
												})}
											</span>
										</PaginationItem>
										<PaginationItem>
											<PaginationNext
												className="cursor-pointer aria-disabled:pointer-events-none aria-disabled:opacity-50"
												aria-disabled={currentPage >= totalPages}
												onClick={() => {
													if (currentPage < totalPages)
														goToPage(currentPage + 1);
												}}
											/>
										</PaginationItem>
									</PaginationContent>
								</Pagination>
							)}
						</>
					)}
				</Deferred>
			</div>

			<Dialog
				open={!!preview}
				onOpenChange={(v) => {
					if (!v) setPreview(null);
				}}
			>
				<DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>
							{t("confirm.title", { count: selected.length })}
						</DialogTitle>
						<DialogDescription>{t("confirm.body")}</DialogDescription>
					</DialogHeader>

					{preview && (
						<Alert variant="destructive">
							<TriangleAlert className="size-4" />
							<AlertDescription>
								{t("confirm.matched", { count: preview.matched })}
							</AlertDescription>
						</Alert>
					)}

					<DialogFooter>
						<Button variant="outline" onClick={() => setPreview(null)}>
							{t("actions.cancel")}
						</Button>
						<Button
							variant="destructive"
							onClick={onRepair}
							disabled={repairing}
						>
							{repairing && <LoaderIcon className="size-4 animate-spin" />}
							{t("actions.repair")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{changeOwnerTarget && (
				<OrderChangeOwnerDialog
					open={!!changeOwnerTarget}
					onOpenChange={(v) => {
						if (!v) setChangeOwnerTarget(null);
					}}
					order={changeOwnerTarget}
				/>
			)}
		</>
	);
}
